import type { AmbitionKey } from './types';
import { DAYS_PER_YEAR } from './time';

/** Balance numbers of the ambition rewards (design pass 1, step 4). "Renown" = prestige for now. */
export const AMBITION_XP_BOOST = 0.1;
export const AMBITION_BOOST_DAYS = DAYS_PER_YEAR;
/** Abandoning an ambition: it cannot be chosen again for one game year. */
export const AMBITION_ABANDON_COOLDOWN_DAYS = DAYS_PER_YEAR;
export const AMBITION_BOND_OPINION = 10;
export const AMBITION_BOND_OPINION_DAYS = 3 * DAYS_PER_YEAR;
/** Merchant house: gold paid instead of the free licence upgrade when no company can be upgraded. */
export const AMBITION_LICENCE_FALLBACK_GOLD = 120;

export interface AmbitionReward {
  gold?: number;
  prestige?: number;
  horses?: number;
  iron?: number;
  /** +10 % XP for every family member for one game year. */
  xpBoost?: boolean;
  /** +10 % squire XP for the house for one game year. */
  squireXpBoost?: boolean;
  /** A free licence upgrade of one company (gold fallback). */
  licence?: boolean;
  /** +10 opinion of the head of the house married into (decays). */
  bondOpinion?: boolean;
  /** A claim on the lowest-rank title of the house married into. */
  bondClaim?: boolean;
}

export interface AmbitionMilestoneDef {
  /** Short requirement, shown to the player. */
  text: string;
  reward: AmbitionReward;
  /** Short reward text. */
  rewardText: string;
}

export interface AmbitionDef {
  key: AmbitionKey;
  name: string;
  summary: string;
  /** Only offered after another ambition has been completed. */
  late: boolean;
  milestones: [AmbitionMilestoneDef, AmbitionMilestoneDef, AmbitionMilestoneDef];
}

export const AMBITION_KEYS: AmbitionKey[] = [
  'seat_at_the_table', 'knights_of_renown', 'land_of_our_own', 'merchant_house', 'bonds_of_blood', 'rise_of_a_dynasty',
];

export const AMBITIONS: Record<AmbitionKey, AmbitionDef> = {
  seat_at_the_table: {
    key: 'seat_at_the_table', name: 'A seat at the table', late: false,
    summary: 'Win your family a voice in the councils of the realm.',
    milestones: [
      { text: 'A member sits on any council', reward: { gold: 20 }, rewardText: '20 gold' },
      { text: 'Two members sit on councils at the same time', reward: { prestige: 40 }, rewardText: '40 prestige' },
      { text: "A member sits on a count's council or higher", reward: { xpBoost: true }, rewardText: '+10 % XP for the whole family for a year' },
    ],
  },
  knights_of_renown: {
    key: 'knights_of_renown', name: 'Knights of renown', late: false,
    summary: "Raise a squire, dub a knight, and see him win a banneret's fame.",
    milestones: [
      { text: 'A member is placed as a squire', reward: { prestige: 15 }, rewardText: '15 prestige' },
      { text: 'A member is a dubbed knight', reward: { horses: 10, iron: 10 }, rewardText: '10 horses and 10 iron' },
      { text: 'A knight of the house reaches Banneret', reward: { squireXpBoost: true }, rewardText: '+10 % squire XP for a year' },
    ],
  },
  land_of_our_own: {
    key: 'land_of_our_own', name: 'Land of our own', late: false,
    summary: 'Win a barony and make it flourish. Only for houses without a barony.',
    milestones: [
      { text: 'A member sends a petition to a liege', reward: { gold: 30 }, rewardText: '30 gold' },
      { text: 'The house holds a barony', reward: { prestige: 60 }, rewardText: '60 prestige' },
      { text: 'A held holding reaches development 4', reward: { gold: 200 }, rewardText: '200 gold toward a development project' },
    ],
  },
  merchant_house: {
    key: 'merchant_house', name: 'Merchant house', late: false,
    summary: 'Found a company and rise through the guilds.',
    milestones: [
      { text: 'The house founds a company', reward: { gold: 20 }, rewardText: '20 gold' },
      { text: 'Journeyman in a guild', reward: { licence: true }, rewardText: 'A free licence upgrade' },
      { text: 'Master in a guild', reward: { prestige: 100 }, rewardText: '100 prestige' },
    ],
  },
  bonds_of_blood: {
    key: 'bonds_of_blood', name: 'Bonds of blood', late: false,
    summary: 'Tie your blood to a great house.',
    milestones: [
      { text: 'A member marries into a landed house', reward: { prestige: 30 }, rewardText: '30 prestige' },
      { text: 'An alliance by marriage with that house', reward: { bondOpinion: true }, rewardText: '+10 opinion with its head for a few years' },
      { text: 'A living grandchild of a count or higher', reward: { bondClaim: true }, rewardText: 'A claim on a title of that house' },
    ],
  },
  rise_of_a_dynasty: {
    key: 'rise_of_a_dynasty', name: 'Rise of a dynasty', late: true,
    summary: 'The late ambition: build a ruling dynasty. Unlocked by completing any other ambition.',
    milestones: [
      { text: 'The house holds a county', reward: { prestige: 100 }, rewardText: '100 prestige' },
      { text: 'The house holds a duchy', reward: { prestige: 200 }, rewardText: '200 prestige' },
      { text: 'The house holds three titles of county rank or higher', reward: { prestige: 300 }, rewardText: '300 prestige' },
    ],
  },
};
